import AppBaseStore from './baseStore'
import { useDjango } from '@/composables/useDjango'
import type { BaseStoreActions, BaseStoreType } from '@/types/storeTypes'
import API from '@/api/apiUrls'
import type { SectionLineGIPoint } from '@/stores/type/sectionLineGIPoint.type'
import { pinia } from '@/modules/pinia'

const { api } = useDjango()
const snackbarStore = useSnackbarStore(pinia)

type State = BaseStoreType<SectionLineGIPoint>
const baseUrl = API.SECTION_GEO_DATA.SECTION_LINES_GI_POINTS.INDEX
const baseStore: BaseStoreType<SectionLineGIPoint> = new AppBaseStore(baseUrl, api, snackbarStore)
const baseStoreActions: BaseStoreActions<SectionLineGIPoint> = baseStore.actions
export const useSectionLinesGIPointsStore = defineStore({
  id: 'sectionLinesGIPoints',
  state: (): State => ({
    ...baseStore.state,
    loading: false,
    filterFields: ['name'],
    addGIPointsDialog: false,
    batchSelect: true,
    reselectSectionLinesDialog: false
  }),
  actions: {
    ...baseStoreActions,
    resetAll() {
      this.$reset()
    },
    toggleReselectSectionLinesDialog() {
      this.reselectSectionLinesDialog = !this.reselectSectionLinesDialog
    },
    removeSelectionsFromSectionLine(sectionLineID: number) {
      this.items = this.items.filter(
        (item: SectionLineGIPoint) => item.section_line !== sectionLineID
      )
    },
    async addGIPointsToSectionLine(giPointIDs: number[], sectionLineID: number) {
      giPointIDs = [...new Set(giPointIDs)]
      const giPointsStore = useGIPointsStore()
      const existingGIPointIDs = this.items
        .filter((item: SectionLineGIPoint) => item.section_line === sectionLineID)
        .map((item: SectionLineGIPoint) => item.gi_point)
      const newGIPointIDs = giPointIDs.filter((id) => !existingGIPointIDs.includes(id))
      await this.bulkCreate(
        newGIPointIDs.map((id) => ({ gi_point: id, section_line: sectionLineID }))
      )
      giPointsStore.selectedItems = []
    },
    async removeGIPointsFromSectionLine(giPointIDs: number[], sectionLineID: number) {
      const giPointsStore = useGIPointsStore()
      try {
        const selectedItems = this.items.filter(
          (item) => item.section_line === sectionLineID && giPointIDs.includes(item.gi_point)
        )
        if (selectedItems.length === 0) {
          return
        }
        await this.bulkDelete(selectedItems)
        giPointsStore.selectedItems = []
      } catch (error) {
        console.error('Failed to remove GI points from section line:', error)
      }
    }
  },
  getters: {
    ...baseStore.getters,
    giPointsSelection: (state: State) => (giPointID: number, sectionLineID: number) => {
      return state.items.find(
        (item) => item.gi_point === giPointID && item.section_line === sectionLineID
      )
    },
    giPointsBySectionLineId: (state: State) => (sectionLineId: number) => {
      const giPointsStore = useGIPointsStore()
      const selections = state.items.filter((item) => item.section_line === sectionLineId)
      return giPointsStore.items.filter((item) =>
        selections.some((selection) => item.id === selection.gi_point)
      )
    }
  }
})
